import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import {
  Alert,
  Box,
  Button,
  Container,
  InputBase,
  Stack,
  Typography
} from '@mui/material';
import {
  alpha,
  type SxProps,
  styled,
  type Theme,
  useTheme
} from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';

import { ESignFieldOverlay } from '@/components/ESign/ESignFieldOverlay';
import { ESignPdfViewer } from '@/components/ESign/ESignPdfViewer';
import { ESignRequiredFieldMarker } from '@/components/ESign/ESignRequiredFieldMarker';
import { getSafeESignDocumentUrl } from '@/components/ESign/getSafeESignDocumentUrl';
import type { ESignPageDimensions } from '@/components/ESign/types';

import {
  EsignFieldKind,
  type PublicSignerSessionResponse,
  type PublicSignerSignatureEntryFieldKind,
  type PublicSignerSignatureEntryPreset,
  type PublicSignerSignatureValue
} from '../publicSignerTypes';
import { isFieldComplete } from '../utils/fieldDraft';
import { getOrderedEditableFields } from '../utils/fieldOrdering';
import {
  getCheckboxFieldValue,
  getRadioGroupFieldValue,
  getSignatureFieldValue,
  getTextFieldValue,
  isFieldEditable,
  isSignatureEntryField,
  isTruthyFieldValue,
  toFieldPosition
} from '../utils/fieldValue';
import { getFieldProgressLabel } from '../utils/presentation';
import type {
  PublicSignerFieldDraftValue,
  PublicSignerFieldDraftValues,
  PublicSignerFieldErrorMap
} from '../utils/types';

import { PublicSignerDisclosureGate } from './PublicSignerDisclosureGate';
import { PublicSignerDocumentHeader } from './PublicSignerDocumentHeader';
import { PublicSignerOverlayButton } from './PublicSignerOverlayButton';
import { PublicSignerRequiredFieldBanner } from './PublicSignerRequiredFieldBanner';
import { PublicSignerSignatureDialog } from './PublicSignerSignatureDialog';
import { PublicSignerSignaturePreview } from './PublicSignerSignaturePreview';

const visuallyHiddenSx = visuallyHidden as SxProps<Theme>;

const BASE_PDF_WIDTH = 1120;
const DEFAULT_PDF_ZOOM = 100;
const MIN_PDF_ZOOM = 50;
const MAX_PDF_ZOOM = 200;
const PDF_ZOOM_STEP = 10;
const MAX_TEXT_FIELD_LENGTH = 500;
const TEXT_FIELD_BASE_FONT_SIZE = 12;
const TEXT_FIELD_HORIZONTAL_PADDING = 3;
const TEXT_FIELD_VERTICAL_PADDING = 2;
const TEXT_FIELD_LINE_HEIGHT = 1.1;
const RADIO_OPTION_TAP_TARGET_SIZE = 44;
const RADIO_OPTION_MARK_SIZE = 18;
const RADIO_OPTION_MARK_OFFSET =
  (RADIO_OPTION_TAP_TARGET_SIZE - RADIO_OPTION_MARK_SIZE) / 2;

const SignerActionRow = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  alignSelf: 'center',
  flex: '0 0 auto',
  justifyContent: 'center',
  marginTop: theme.spacing(1.5),
  width: 'min(320px, 100%)',
  '& .MuiButton-root': {
    flex: '1 1 0',
    minWidth: 0,
    whiteSpace: 'nowrap'
  },
  [theme.breakpoints.up('sm')]: {
    bottom: theme.spacing(2),
    marginTop: 0,
    position: 'fixed',
    right: theme.spacing(2),
    width: 'auto',
    zIndex: theme.zIndex.speedDial,
    '& .MuiButton-root': {
      flex: '0 0 auto',
      minWidth: 120,
      width: 'auto'
    }
  },
  [theme.breakpoints.up('md')]: {
    bottom: theme.spacing(3),
    right: theme.spacing(3)
  }
}));

const PreviousFieldButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper
}));

function getPdfPointScale(dimensions: ESignPageDimensions) {
  if (dimensions.sourceWidth > 0) {
    return dimensions.renderedWidth / dimensions.sourceWidth;
  }
  if (dimensions.sourceHeight > 0) {
    return dimensions.renderedHeight / dimensions.sourceHeight;
  }
  return 1;
}

function getFieldBoxPoints(
  field: { x: number; y: number; width: number; height: number },
  dimensions: ESignPageDimensions
) {
  return {
    x: field.x * dimensions.sourceWidth,
    y: field.y * dimensions.sourceHeight,
    width: field.width * dimensions.sourceWidth,
    height: field.height * dimensions.sourceHeight
  };
}

function isMultilineTextField(value: string, fieldHeightPoints: number) {
  const innerHeight = Math.max(
    0,
    fieldHeightPoints - TEXT_FIELD_VERTICAL_PADDING * 2
  );
  return (
    value.includes('\n') ||
    innerHeight >= TEXT_FIELD_BASE_FONT_SIZE * TEXT_FIELD_LINE_HEIGHT * 2
  );
}

function getTextPreviewLineClamp(fieldHeightPoints: number) {
  const innerHeight = Math.max(
    0,
    fieldHeightPoints - TEXT_FIELD_VERTICAL_PADDING * 2
  );
  return Math.max(
    1,
    Math.floor(
      innerHeight / (TEXT_FIELD_BASE_FONT_SIZE * TEXT_FIELD_LINE_HEIGHT)
    )
  );
}

function getEditableFieldAriaLabel(field: {
  is_required: boolean;
  label: string;
}) {
  return field.is_required ? `${field.label}, required` : field.label;
}

export function PublicSignerWorkspace({
  blockedRequiredFieldId,
  fieldDraftValues,
  fieldErrors,
  isSubmitting,
  onAdvanceToNextField,
  onAdvanceToPreviousField,
  onContinueDisclosure,
  onDismissBlockedRequiredField,
  onFieldDraftChange,
  onOpenSignatureField,
  onRefetchSession,
  onSelectField,
  onSignatureDraftChange,
  onSignatureApplied,
  onSubmit,
  openSignatureFieldId,
  progressSaveErrorMessage,
  progressSaveStatus,
  requiresDisclosure,
  selectedFieldId,
  selectedFieldScrollRequestId,
  session,
  signatureEntryPresets
}: {
  blockedRequiredFieldId: string | null;
  fieldDraftValues: PublicSignerFieldDraftValues;
  fieldErrors: PublicSignerFieldErrorMap;
  isSubmitting: boolean;
  onAdvanceToNextField: () => void;
  onAdvanceToPreviousField: () => void;
  onContinueDisclosure: () => void;
  onDismissBlockedRequiredField: () => void;
  onFieldDraftChange: (
    fieldId: string,
    value: PublicSignerFieldDraftValue
  ) => void;
  onOpenSignatureField: (fieldId: string | null) => void;
  onRefetchSession: () => Promise<unknown>;
  onSelectField: (fieldId: string) => void;
  onSignatureDraftChange: (
    fieldId: string,
    value: PublicSignerFieldDraftValue
  ) => void;
  onSignatureApplied: (
    fieldKind: PublicSignerSignatureEntryFieldKind,
    value: PublicSignerSignatureValue | null
  ) => void;
  onSubmit: () => Promise<void>;
  openSignatureFieldId: string | null;
  progressSaveErrorMessage: string | null;
  progressSaveStatus: 'saved' | 'saving' | null;
  requiresDisclosure: boolean;
  selectedFieldId: string | null;
  selectedFieldScrollRequestId: number;
  session: PublicSignerSessionResponse;
  signatureEntryPresets: Record<
    PublicSignerSignatureEntryFieldKind,
    PublicSignerSignatureEntryPreset
  >;
}) {
  const theme = useTheme();
  const [pdfContainerEl, setPdfContainerEl] = useState<HTMLDivElement | null>(
    null
  );
  const orderedEditableFields = useMemo(
    () => getOrderedEditableFields(session.fields),
    [session.fields]
  );
  const [containerWidth, setContainerWidth] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [pdfZoom, setPdfZoom] = useState(DEFAULT_PDF_ZOOM);
  const previewUrl = getSafeESignDocumentUrl(session.document_url);
  const selectedFieldScrollTarget = useMemo(
    () =>
      selectedFieldId
        ? {
            fieldId: selectedFieldId,
            requestId: selectedFieldScrollRequestId
          }
        : null,
    [selectedFieldId, selectedFieldScrollRequestId]
  );
  const signatureDialogField = useMemo(
    () =>
      session.fields.find(
        (field) =>
          field.id === openSignatureFieldId && isSignatureEntryField(field)
      ) ?? null,
    [openSignatureFieldId, session.fields]
  );
  const signatureDialogFieldKind =
    signatureDialogField && isSignatureEntryField(signatureDialogField)
      ? signatureDialogField.field_kind
      : null;
  const signatureDialogPreset = signatureDialogFieldKind
    ? signatureEntryPresets[signatureDialogFieldKind]
    : null;
  const completedEditableFieldCount = useMemo(
    () =>
      orderedEditableFields.filter((field) =>
        isFieldComplete(field, fieldDraftValues[field.id])
      ).length,
    [fieldDraftValues, orderedEditableFields]
  );
  const allRequiredComplete = useMemo(
    () =>
      orderedEditableFields.every(
        (field) =>
          !field.is_required ||
          isFieldComplete(field, fieldDraftValues[field.id])
      ),
    [fieldDraftValues, orderedEditableFields]
  );
  const fieldProgressLabel = useMemo(
    () =>
      getFieldProgressLabel(
        completedEditableFieldCount,
        orderedEditableFields.length
      ),
    [completedEditableFieldCount, orderedEditableFields.length]
  );
  const isFieldProgressComplete =
    completedEditableFieldCount >= orderedEditableFields.length &&
    orderedEditableFields.length > 0;
  const fitPdfWidth = useMemo(
    () => Math.min(Math.max(containerWidth, 320), BASE_PDF_WIDTH),
    [containerWidth]
  );
  const pdfWidth = useMemo(
    () => Math.round((fitPdfWidth * pdfZoom) / 100),
    [fitPdfWidth, pdfZoom]
  );
  const handleZoomOut = useCallback(() => {
    setPdfZoom((currentZoom) =>
      Math.max(MIN_PDF_ZOOM, currentZoom - PDF_ZOOM_STEP)
    );
  }, []);
  const handleZoomIn = useCallback(() => {
    setPdfZoom((currentZoom) =>
      Math.min(MAX_PDF_ZOOM, currentZoom + PDF_ZOOM_STEP)
    );
  }, []);

  useEffect(() => {
    if (!pdfContainerEl) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(pdfContainerEl);

    return () => observer.disconnect();
  }, [pdfContainerEl]);

  useEffect(() => {
    const container = pdfContainerEl;
    if (!container) {
      return;
    }

    const updateCurrentPage = () => {
      // Marker is always present; .react-pdf__Page only mounts near the viewport.
      const pages = container.querySelectorAll('[data-esign-pdf-page-number]');
      if (pages.length === 0 || numPages === 0) {
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.top + containerRect.height / 2;
      let closestPage = 1;
      let closestDistance = Number.POSITIVE_INFINITY;
      pages.forEach((page) => {
        const rect = page.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - containerCenter);
        const pageNum = Number(page.getAttribute('data-esign-pdf-page-number'));
        if (distance < closestDistance && pageNum) {
          closestDistance = distance;
          closestPage = pageNum;
        }
      });
      setCurrentPage(closestPage);
    };

    container.addEventListener('scroll', updateCurrentPage, { passive: true });

    return () => {
      container.removeEventListener('scroll', updateCurrentPage);
    };
  }, [pdfContainerEl, numPages]);

  useEffect(() => {
    if (!pdfContainerEl || !selectedFieldScrollTarget || requiresDisclosure) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const target = Array.from(
        pdfContainerEl.querySelectorAll<HTMLElement>(
          '[data-public-signer-field-id]'
        )
      ).find(
        (element) =>
          element.dataset.publicSignerFieldId ===
          selectedFieldScrollTarget.fieldId
      );
      if (!target) {
        return;
      }

      const containerRect = pdfContainerEl.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const nextScrollTop =
        pdfContainerEl.scrollTop +
        targetRect.top -
        containerRect.top -
        pdfContainerEl.clientHeight / 2 +
        targetRect.height / 2;

      if (typeof pdfContainerEl.scrollTo === 'function') {
        pdfContainerEl.scrollTo({
          behavior: 'smooth',
          top: Math.max(0, nextScrollTop)
        });
        return;
      }

      pdfContainerEl.scrollTop = Math.max(0, nextScrollTop);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [pdfContainerEl, requiresDisclosure, selectedFieldScrollTarget]);

  const renderOverlay = useCallback(
    (pageNumber: number, dimensions: ESignPageDimensions) => {
      const pageFields = session.fields.filter(
        (field) => field.page_index + 1 === pageNumber
      );

      if (pageFields.length === 0) {
        return null;
      }

      return (
        <>
          {pageFields.map((field) => {
            const draftValue = fieldDraftValues[field.id];
            const isSelected = selectedFieldId === field.id;
            const isEditable = isFieldEditable(field);
            const fieldErrorMessage =
              fieldErrors[field.id] ??
              (blockedRequiredFieldId === field.id
                ? 'This field is required.'
                : undefined);
            const fieldHasError = Boolean(fieldErrorMessage);
            const showRequiredAsterisk = isEditable && field.is_required;
            const editableFieldAriaLabel = getEditableFieldAriaLabel(field);
            const color = fieldHasError
              ? theme.palette.error.main
              : isEditable
                ? theme.palette.primary.main
                : theme.palette.text.secondary;
            const isCheckbox = field.field_kind === EsignFieldKind.Checkbox;
            const fieldPosition = toFieldPosition(field);
            const fieldBoxPoints = getFieldBoxPoints(fieldPosition, dimensions);
            const pdfPointScale = getPdfPointScale(dimensions);
            const textValue = getTextFieldValue(draftValue);
            const persistedTextValue = getTextFieldValue(field.value);
            const previewTextValue = isEditable
              ? textValue
              : persistedTextValue;
            const signatureValue = isSignatureEntryField(field)
              ? getSignatureFieldValue(isEditable ? draftValue : field.value)
              : null;
            const fieldSupportsMultilineText =
              field.field_kind === EsignFieldKind.Text &&
              isMultilineTextField(previewTextValue, fieldBoxPoints.height);
            const textPreviewLineClamp = getTextPreviewLineClamp(
              fieldBoxPoints.height
            );
            const textPreviewSx: SxProps<Theme> = {
              boxSizing: 'border-box',
              display: fieldSupportsMultilineText ? '-webkit-box' : 'block',
              fontSize: TEXT_FIELD_BASE_FONT_SIZE * pdfPointScale,
              fontWeight: 400,
              height: fieldSupportsMultilineText ? '100%' : undefined,
              lineHeight: TEXT_FIELD_LINE_HEIGHT,
              maxWidth: '100%',
              overflow: 'hidden',
              px: `${TEXT_FIELD_HORIZONTAL_PADDING * pdfPointScale}px`,
              py: fieldSupportsMultilineText
                ? `${TEXT_FIELD_VERTICAL_PADDING * pdfPointScale}px`
                : 0,
              textOverflow: 'ellipsis',
              textAlign: 'left',
              whiteSpace: fieldSupportsMultilineText ? 'pre-wrap' : 'nowrap',
              width: '100%',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: fieldSupportsMultilineText
                ? textPreviewLineClamp
                : 1
            };

            if (field.field_kind === EsignFieldKind.RadioGroup) {
              return renderRadioGroupField({
                color,
                dimensions,
                draftValue,
                field,
                fieldHasError,
                fieldErrorMessage,
                isEditable: isFieldEditable(field),
                isSelected,
                onFieldDraftChange,
                onSelectField,
                theme
              });
            }

            let content: ReactNode;

            if (!isEditable) {
              if (isSignatureEntryField(field)) {
                content = (
                  <PublicSignerSignaturePreview
                    boxHeight={fieldBoxPoints.height}
                    boxWidth={fieldBoxPoints.width}
                    fontScale={pdfPointScale}
                    placeholder={field.label}
                    value={signatureValue}
                  />
                );
              } else if (field.field_kind === EsignFieldKind.Checkbox) {
                content = isTruthyFieldValue(field.value) ? (
                  <CheckRoundedIcon sx={{ fontSize: 18 }} />
                ) : null;
              } else {
                content = (
                  <Typography sx={textPreviewSx}>
                    {persistedTextValue.trim() || field.label}
                  </Typography>
                );
              }
            } else if (field.field_kind === EsignFieldKind.Checkbox) {
              const isChecked = getCheckboxFieldValue(draftValue);

              content = (
                <PublicSignerOverlayButton
                  ariaChecked={isChecked}
                  ariaLabel={editableFieldAriaLabel}
                  errorMessage={fieldErrorMessage}
                  fieldId={field.id}
                  hasError={fieldHasError}
                  onClick={() => {
                    onSelectField(field.id);
                    onFieldDraftChange(field.id, !isChecked);
                  }}
                  role="checkbox"
                >
                  {isChecked ? (
                    <CheckRoundedIcon sx={{ fontSize: 18 }} />
                  ) : null}
                </PublicSignerOverlayButton>
              );
            } else if (isSignatureEntryField(field)) {
              content = (
                <PublicSignerOverlayButton
                  ariaLabel={editableFieldAriaLabel}
                  errorMessage={fieldErrorMessage}
                  fieldId={field.id}
                  hasError={fieldHasError}
                  onClick={() => {
                    onSelectField(field.id);
                    onOpenSignatureField(field.id);
                  }}
                >
                  <PublicSignerSignaturePreview
                    boxHeight={fieldBoxPoints.height}
                    boxWidth={fieldBoxPoints.width}
                    fontScale={pdfPointScale}
                    placeholder={field.label}
                    value={signatureValue}
                  />
                </PublicSignerOverlayButton>
              );
            } else if (isSelected) {
              const fieldErrorId = fieldErrorMessage
                ? `public-signer-field-error-${field.id}`
                : undefined;
              content = (
                <>
                  <InputBase
                    data-public-signer-field-id={field.id}
                    inputRef={(
                      el: HTMLInputElement | HTMLTextAreaElement | null
                    ) => {
                      el?.focus({ preventScroll: true });
                    }}
                    inputProps={{
                      'aria-describedby': fieldErrorId,
                      'aria-label': field.label,
                      'aria-invalid': fieldHasError || undefined,
                      'aria-required': field.is_required || undefined,
                      maxLength: MAX_TEXT_FIELD_LENGTH,
                      required: field.is_required || undefined,
                      ...(fieldSupportsMultilineText
                        ? undefined
                        : { type: 'text' })
                    }}
                    multiline={fieldSupportsMultilineText}
                    onChange={(event) => {
                      onFieldDraftChange(field.id, event.target.value);
                    }}
                    onFocus={() => {
                      onSelectField(field.id);
                    }}
                    sx={{
                      alignItems: fieldSupportsMultilineText
                        ? 'flex-start'
                        : 'center',
                      boxSizing: 'border-box',
                      color: 'text.primary',
                      fontSize: TEXT_FIELD_BASE_FONT_SIZE * pdfPointScale,
                      fontWeight: 400,
                      height: '100%',
                      px: `${TEXT_FIELD_HORIZONTAL_PADDING * pdfPointScale}px`,
                      py: fieldSupportsMultilineText
                        ? `${TEXT_FIELD_VERTICAL_PADDING * pdfPointScale}px`
                        : 0,
                      width: '100%',
                      ...(fieldHasError
                        ? {
                            backgroundColor: alpha(
                              theme.palette.error.main,
                              0.08
                            )
                          }
                        : undefined),
                      '& input': {
                        p: 0,
                        textAlign: 'left'
                      },
                      '& textarea': {
                        lineHeight: TEXT_FIELD_LINE_HEIGHT,
                        p: 0,
                        resize: 'none',
                        textAlign: 'left'
                      }
                    }}
                    value={textValue}
                  />
                  {fieldErrorId ? (
                    <Box
                      component="span"
                      id={fieldErrorId}
                      sx={visuallyHiddenSx}
                    >
                      {fieldErrorMessage}
                    </Box>
                  ) : null}
                </>
              );
            } else {
              const displayValue = textValue.trim() || field.label;

              content = (
                <PublicSignerOverlayButton
                  ariaLabel={editableFieldAriaLabel}
                  errorMessage={fieldErrorMessage}
                  fieldId={field.id}
                  hasError={fieldHasError}
                  onClick={() => {
                    onSelectField(field.id);
                  }}
                >
                  <Typography sx={textPreviewSx}>{displayValue}</Typography>
                </PublicSignerOverlayButton>
              );
            }

            return (
              <ESignFieldOverlay
                color={color}
                hoverContent={
                  isFieldEditable(field)
                    ? `Assigned to you • Page ${field.page_index + 1}`
                    : `Read only • Page ${field.page_index + 1}`
                }
                isCheckbox={isCheckbox}
                isSelected={isSelected}
                key={field.id}
                pageDimensions={dimensions}
                position={fieldPosition}
                style={{
                  alignItems:
                    field.field_kind === EsignFieldKind.Text &&
                    fieldSupportsMultilineText
                      ? 'flex-start'
                      : 'center',
                  backgroundColor: isCheckbox
                    ? getCheckboxFieldValue(draftValue)
                      ? alpha(color, 0.14)
                      : 'transparent'
                    : fieldHasError
                      ? alpha(theme.palette.error.main, 0.12)
                      : alpha(color, 0.1),
                  border: `${fieldHasError ? 2 : 1}px solid ${color}`,
                  boxShadow: fieldHasError
                    ? `0 0 0 3px ${alpha(theme.palette.error.main, 0.14)}`
                    : undefined,
                  borderRadius: isCheckbox ? 0 : 6,
                  color: theme.palette.text.primary,
                  display: 'flex',
                  justifyContent:
                    field.field_kind === EsignFieldKind.Text &&
                    fieldSupportsMultilineText
                      ? 'flex-start'
                      : 'center',
                  ...(signatureValue?.type === 'typed'
                    ? { overflow: 'visible' }
                    : undefined),
                  ...(showRequiredAsterisk
                    ? { overflow: 'visible' }
                    : undefined),
                  padding: 0,
                  textAlign: 'center',
                  userSelect: 'none'
                }}
              >
                {showRequiredAsterisk ? <ESignRequiredFieldMarker /> : null}
                {content}
              </ESignFieldOverlay>
            );
          })}
        </>
      );
    },
    [
      fieldDraftValues,
      fieldErrors,
      blockedRequiredFieldId,
      onFieldDraftChange,
      onOpenSignatureField,
      onSelectField,
      selectedFieldId,
      session.fields,
      theme
    ]
  );

  if (requiresDisclosure) {
    return (
      <PublicSignerDisclosureGate
        onContinue={onContinueDisclosure}
        session={session}
      />
    );
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        px: { xs: 1, md: 2 },
        py: { xs: 2, md: 3 }
      }}
    >
      <Stack gap={1.5} sx={{ flex: 1, minHeight: 0 }}>
        <PublicSignerDocumentHeader
          canZoomIn={pdfZoom < MAX_PDF_ZOOM}
          canZoomOut={pdfZoom > MIN_PDF_ZOOM}
          currentPage={currentPage}
          facilityName={session.facility_name}
          fieldProgressLabel={fieldProgressLabel}
          isFieldProgressComplete={isFieldProgressComplete}
          numPages={numPages}
          pdfZoom={pdfZoom}
          progressSaveStatus={progressSaveStatus}
          requestTitle={session.request_title}
          sessionExpiresAt={session.session_expires_at}
          signerName={session.signer_name}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
        />

        <Box
          sx={(theme) => ({
            backgroundColor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '20px',
            display: 'flex',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden'
          })}
        >
          <Stack spacing={0} sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
            {progressSaveErrorMessage ? (
              <Alert
                data-testid="public-signer-progress-save-error"
                severity="error"
              >
                {progressSaveErrorMessage}
              </Alert>
            ) : null}
            {blockedRequiredFieldId ? (
              <PublicSignerRequiredFieldBanner
                onDismiss={onDismissBlockedRequiredField}
              />
            ) : null}
            {previewUrl ? (
              <Box
                ref={setPdfContainerEl}
                sx={(theme) => ({
                  backgroundColor: theme.palette.info.main,
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  overflow: 'auto',
                  p: { xs: 1, md: 1.5 }
                })}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%'
                  }}
                >
                  <ESignPdfViewer
                    onDocumentLoad={(pages) => {
                      setCurrentPage(1);
                      setNumPages(pages);
                    }}
                    onUrlExpired={async () => {
                      await onRefetchSession();
                    }}
                    renderOverlay={renderOverlay}
                    url={previewUrl}
                    width={pdfWidth}
                  />
                </Box>
              </Box>
            ) : (
              <Stack p={2}>
                <Alert severity="info">
                  The preview document is not available yet for this signing
                  session.
                </Alert>
              </Stack>
            )}
          </Stack>
        </Box>
      </Stack>

      <PublicSignerSignatureDialog
        error={
          signatureDialogField
            ? fieldErrors[signatureDialogField.id]
            : undefined
        }
        field={signatureDialogField}
        initialValue={
          signatureDialogField
            ? getSignatureFieldValue(fieldDraftValues[signatureDialogField.id])
            : null
        }
        presetDrawnImageDataUrl={
          signatureDialogPreset?.drawnImageDataUrl ?? null
        }
        presetMode={signatureDialogPreset?.appliedMode ?? null}
        presetTypedName={signatureDialogPreset?.typedName ?? null}
        onClose={() => {
          onOpenSignatureField(null);
        }}
        onSave={(value) => {
          if (!signatureDialogField || !signatureDialogFieldKind) {
            return;
          }

          onSignatureDraftChange(signatureDialogField.id, value);
          onSignatureApplied(signatureDialogFieldKind, value);
          onOpenSignatureField(null);
          onSelectField(signatureDialogField.id);
        }}
      />

      <SignerActionRow direction="row" spacing={{ xs: 1, sm: 2 }}>
        {orderedEditableFields.length > 1 ? (
          <PreviousFieldButton
            color="secondary"
            onClick={onAdvanceToPreviousField}
            variant="outlined"
          >
            Previous field
          </PreviousFieldButton>
        ) : null}
        {allRequiredComplete ? (
          <Button
            color="primary"
            loading={isSubmitting}
            onClick={async () => {
              await onSubmit();
            }}
            variant="contained"
          >
            Complete signing
          </Button>
        ) : (
          <Button
            color="primary"
            onClick={onAdvanceToNextField}
            variant="contained"
          >
            {orderedEditableFields.length > 1 ? 'Next field' : 'Go to field'}
          </Button>
        )}
      </SignerActionRow>
    </Container>
  );
}

function renderRadioGroupField({
  color,
  dimensions,
  draftValue,
  field,
  fieldHasError,
  fieldErrorMessage,
  isEditable,
  isSelected,
  onFieldDraftChange,
  onSelectField,
  theme
}: {
  color: string;
  dimensions: ESignPageDimensions;
  draftValue: PublicSignerFieldDraftValue | undefined;
  field: PublicSignerSessionResponse['fields'][number];
  fieldHasError: boolean;
  fieldErrorMessage: string | undefined;
  isEditable: boolean;
  isSelected: boolean;
  onFieldDraftChange: (
    fieldId: string,
    value: PublicSignerFieldDraftValue
  ) => void;
  onSelectField: (fieldId: string) => void;
  theme: Theme;
}) {
  if (field.field_kind !== EsignFieldKind.RadioGroup) {
    return null;
  }

  const options = field.options ?? [];
  const selectedOptionId = isEditable
    ? getRadioGroupFieldValue(draftValue)
    : getRadioGroupFieldValue(field.value);
  const showRequiredAsterisk = isEditable && field.is_required;
  const fieldErrorId =
    fieldHasError && fieldErrorMessage
      ? `public-signer-field-error-${field.id}`
      : undefined;

  return (
    <Fragment key={field.id}>
      <ESignFieldOverlay
        color={color}
        hoverContent={
          isEditable
            ? `Assigned to you • Page ${field.page_index + 1}`
            : `Read only • Page ${field.page_index + 1}`
        }
        isSelected={isSelected}
        pageDimensions={dimensions}
        position={toFieldPosition(field)}
        style={{
          backgroundColor: 'transparent',
          border: `${fieldHasError ? 2 : 1}px dashed ${color}`,
          boxShadow: fieldHasError
            ? `0 0 0 3px ${alpha(theme.palette.error.main, 0.14)}`
            : undefined,
          borderRadius: 6,
          color: theme.palette.text.primary,
          overflow: showRequiredAsterisk ? 'visible' : undefined,
          padding: 0,
          pointerEvents: 'none',
          userSelect: 'none'
        }}
      >
        {showRequiredAsterisk ? <ESignRequiredFieldMarker /> : null}
        <Box sx={visuallyHiddenSx} aria-hidden>
          {field.label}
        </Box>
      </ESignFieldOverlay>
      <Box
        aria-describedby={fieldErrorId}
        aria-invalid={fieldHasError || undefined}
        aria-label={getEditableFieldAriaLabel(field)}
        aria-required={field.is_required || undefined}
        component="div"
        role={isEditable ? 'radiogroup' : undefined}
        sx={{ display: 'contents' }}
      >
        {options.map((option) => {
          const optionId = option.id;
          const isOptionSelected = optionId === selectedOptionId;
          const isClickable = isEditable;
          const nextValue =
            !field.is_required && isOptionSelected ? null : optionId;
          return (
            <ESignFieldOverlay
              key={`option-${optionId}`}
              color={color}
              hoverContent={option.label}
              isCheckbox
              pageDimensions={dimensions}
              position={{
                x: option.x,
                y: option.y,
                width: option.width,
                height: option.height,
                page: option.page_index + 1
              }}
              style={{
                alignItems: 'center',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '50%',
                cursor: isClickable ? 'pointer' : 'default',
                display: 'flex',
                justifyContent: 'center',
                overflow: 'visible',
                padding: 0,
                userSelect: 'none'
              }}
            >
              {isClickable ? (
                <PublicSignerOverlayButton
                  ariaLabel={option.label}
                  ariaChecked={isOptionSelected}
                  errorId={fieldErrorId}
                  errorMessage={fieldErrorMessage}
                  fieldId={field.id}
                  hasError={fieldHasError}
                  onClick={() => {
                    onSelectField(field.id);
                    onFieldDraftChange(field.id, nextValue);
                  }}
                  renderErrorMessage={false}
                  role="radio"
                  sx={{
                    borderRadius: `${RADIO_OPTION_TAP_TARGET_SIZE / 2}px`,
                    height: 'auto',
                    left: '50%',
                    minHeight: RADIO_OPTION_TAP_TARGET_SIZE,
                    minWidth: RADIO_OPTION_TAP_TARGET_SIZE,
                    overflow: 'visible',
                    position: 'absolute',
                    top: '50%',
                    transform: `translate(-${RADIO_OPTION_TAP_TARGET_SIZE / 2}px, -50%)`,
                    width: 'max-content'
                  }}
                >
                  <RadioOptionContent
                    color={color}
                    selected={isOptionSelected}
                  />
                </PublicSignerOverlayButton>
              ) : (
                <RadioOptionContent color={color} selected={isOptionSelected} />
              )}
            </ESignFieldOverlay>
          );
        })}
        {fieldErrorId ? (
          <Box component="span" id={fieldErrorId} sx={visuallyHiddenSx}>
            {fieldErrorMessage}
          </Box>
        ) : null}
      </Box>
    </Fragment>
  );
}

function RadioOptionContent({
  color,
  selected
}: {
  color: string;
  selected: boolean;
}) {
  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 0.75,
        justifyContent: 'flex-start',
        minHeight: RADIO_OPTION_TAP_TARGET_SIZE,
        minWidth: RADIO_OPTION_TAP_TARGET_SIZE,
        pl: `${RADIO_OPTION_MARK_OFFSET}px`
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: 'white',
          border: `2px solid ${color}`,
          borderRadius: '50%',
          display: 'flex',
          flexShrink: 0,
          height: RADIO_OPTION_MARK_SIZE,
          justifyContent: 'center',
          width: RADIO_OPTION_MARK_SIZE
        }}
      >
        {selected ? (
          <Box
            sx={{
              backgroundColor: color,
              borderRadius: '50%',
              height: '55%',
              width: '55%'
            }}
          />
        ) : null}
      </Box>
    </Box>
  );
}
